/**
 * JSONPath Parser for V2 Templates
 *
 * Handles the jsonpath<data-type> syntax used in V2 templates
 * Example: "$.primary_keys<array>" -> { jsonpath: "$.primary_keys", dataType: "array" }
 */

export interface ParsedJsonPath {
  jsonpath: string;
  dataType: string;
  original: string;
}

/**
 * Parse a jsonpath<data-type> string into its components
 * @param input - String in format "jsonpath<data-type>"
 * @returns ParsedJsonPath object or null if invalid format
 */
export function parseJsonPathWithType(input: string): ParsedJsonPath | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  // Match pattern: jsonpath<data-type>
  const match = input.match(/^(.+)<([^>]+)>$/);
  if (!match) {
    return null;
  }

  const [, jsonpath, dataType] = match;
  return {
    jsonpath: jsonpath.trim(),
    dataType: dataType.trim(),
    original: input,
  };
}

/**
 * Extract value from data using JSONPath and cast to specified data type
 * @param data - The data object to extract from
 * @param parsedPath - Parsed JSONPath with data type
 * @returns Extracted and cast value
 */
export function extractValueWithType(data: any, parsedPath: ParsedJsonPath): any {
  if (!data || !parsedPath) {
    return null;
  }

  // Simple JSONPath implementation for basic cases
  // For production, you might want to use a library like jsonpath-plus
  const { jsonpath, dataType } = parsedPath;

  let value: any;

  // Handle simple JSONPath expressions
  if (jsonpath === '$') {
    value = data;
  } else if (jsonpath.startsWith('$.')) {
    const path = jsonpath.substring(2); // Remove "$."
    value = getNestedValue(data, path);
  } else {
    // For more complex JSONPath expressions, you'd need a proper JSONPath library
    console.warn('Complex JSONPath expressions not yet supported:', jsonpath);
    return null;
  }

  // Cast to specified data type
  return castToDataType(value, dataType);
}

/**
 * Get nested value from object using dot notation
 * @param obj - The object to traverse
 * @param path - Dot-separated path (e.g., "tables[0].name")
 * @returns The value at the path or undefined
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) {
    return undefined;
  }

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }

    // Handle array access like "tables[0]"
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayName, index] = arrayMatch;
      current = current[arrayName];
      if (Array.isArray(current)) {
        current = current[parseInt(index, 10)];
      } else {
        return undefined;
      }
    } else {
      current = current[part];
    }
  }

  return current;
}

/**
 * Cast value to specified data type
 * @param value - The value to cast
 * @param dataType - The target data type
 * @returns Cast value
 */
function castToDataType(value: any, dataType: string): any {
  if (value === null || value === undefined) {
    return null;
  }

  switch (dataType.toLowerCase()) {
    case 'string':
      return String(value);
    case 'number':
    case 'int':
    case 'integer':
      return Number(value);
    case 'boolean':
    case 'bool':
      return Boolean(value);
    case 'array':
      return Array.isArray(value) ? value : [value];
    case 'object':
      return typeof value === 'object' ? value : { value };
    default:
      console.warn('Unknown data type for casting:', dataType);
      return value;
  }
}

/**
 * Process primary keys for V2 templates
 * @param feedData - The feed data from discovery
 * @param primaryKeysConfig - The primary keys configuration (could be string, array, or jsonpath<type>)
 * @returns Processed primary keys array
 */
export function processPrimaryKeys(feedData: any, primaryKeysConfig: any): string[] {
  if (!primaryKeysConfig) {
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(primaryKeysConfig)) {
    return primaryKeysConfig.map((pk) => String(pk));
  }

  // If it's a string, check if it's a jsonpath<type> format
  if (typeof primaryKeysConfig === 'string') {
    const parsed = parseJsonPathWithType(primaryKeysConfig);
    if (parsed) {
      // It's a jsonpath<type> format, extract from feedData
      const extracted = extractValueWithType(feedData, parsed);
      return Array.isArray(extracted) ? extracted.map((pk) => String(pk)) : [String(extracted)];
    } else {
      // It's a simple string, return as single-item array
      return [primaryKeysConfig];
    }
  }

  // Fallback: convert to string and return as single-item array
  return [String(primaryKeysConfig)];
}
